/**
 * Sticky buy bar — appears once the watched target scrolls above the fixed
 * header. The target differs by breakpoint:
 *   • Desktop / tablet: the inline "Adaugă în coș" button (data-watch-target).
 *     The buy column is a sticky right rail near the top, so the bar appears
 *     after it scrolls past.
 *   • Mobile (≤640px): the gallery column (data-watch-target-mobile). The
 *     layout stacks gallery → selectors → purchase, putting the inline button
 *     far down the page; watching the button would only reveal the bar AFTER
 *     the user scrolls past the CTA (too late). Watching the gallery instead
 *     shows the bar as soon as the photos scroll away, while the user reads
 *     the specs below.
 *
 * The fixed-header offset comes from the theme's CSS vars
 * (--header-height + --announcement-bar-height, multiplied by
 * --header-is-sticky) so behavior stays in sync across breakpoints
 * regardless of whether the header is sticky.
 */
class ProductStickyBar extends HTMLElement {
  constructor() {
    super();
    this._visibleClass = 'product-sticky-bar--visible';
    this._observer = null;
    this._mql = null;
    this._onBreakpointChange = this._setupObserver.bind(this);
  }

  connectedCallback() {
    // Re-pick the watch target when crossing the mobile breakpoint so the bar
    // tracks the gallery on phones and the buy button on larger screens.
    this._mql = window.matchMedia('(max-width: 640px)');
    this._mql.addEventListener('change', this._onBreakpointChange);
    this._setupObserver();
  }

  _setupObserver() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }

    const desktopSel = this.dataset.watchTarget || '.buy-button';
    const mobileSel = this.dataset.watchTargetMobile;
    const useMobile = !!mobileSel && !!this._mql && this._mql.matches;
    const target =
      document.querySelector(useMobile ? mobileSel : desktopSel) ||
      document.querySelector(desktopSel);
    if (!target) {
      this.classList.remove(this._visibleClass);
      this.setAttribute('aria-hidden', 'true');
      return;
    }

    const offset = this._getHeaderOffset();

    this._observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        // Re-read the offset on each callback so a header that changes
        // between sticky / non-sticky (e.g. on resize) stays accurate.
        const liveOffset = this._getHeaderOffset();
        // Show the sticky bar once the watched target has scrolled past the
        // header bottom (its bottom edge above the header line — fully covered
        // or above the viewport top). On desktop the target is the buy button;
        // on mobile it's the gallery column.
        const shouldShow =
          !entry.isIntersecting && entry.boundingClientRect.bottom <= liveOffset;
        this.classList.toggle(this._visibleClass, shouldShow);
        this.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      },
      {
        // Shrink the root from the top by the fixed-header height so that
        // intersection ends right when the target slides under the header.
        rootMargin: `-${offset}px 0px 0px 0px`,
        threshold: 0,
      }
    );

    this._observer.observe(target);
  }

  _getHeaderOffset() {
    const cs = getComputedStyle(document.documentElement);
    const isSticky = parseFloat(cs.getPropertyValue('--header-is-sticky')) || 0;
    if (isSticky <= 0) return 0;
    const headerH = parseFloat(cs.getPropertyValue('--header-height')) || 0;
    const annH = parseFloat(cs.getPropertyValue('--announcement-bar-height')) || 0;
    return headerH + annH;
  }

  disconnectedCallback() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
    if (this._mql) {
      this._mql.removeEventListener('change', this._onBreakpointChange);
      this._mql = null;
    }
  }
}

if (!customElements.get('cos-sticky-bar')) {
  customElements.define('cos-sticky-bar', ProductStickyBar);
}
